/**
 * RenameStrategyModal — rename a user strategy (SHA-43 item 1).
 *
 * IMPORTANT: this renames the STRATEGY (user_strategies.name) via the existing
 * PUT /api/user-strategies/:id endpoint. It must never be confused with
 * renaming an aggregation/portfolio — the old chat path wrongly renamed the
 * aggregation instead of the strategy; this component is strategy-only.
 */

import { useEffect, useState } from 'react';
import { Modal } from '../Modal/Modal.js';
import { useUpdateUserStrategy } from '../../hooks/useUserStrategies.js';

interface RenameStrategyModalProps {
  isOpen: boolean;
  strategyId: string;
  currentName: string;
  onClose: () => void;
}

export function RenameStrategyModal({ isOpen, strategyId, currentName, onClose }: RenameStrategyModalProps) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useUpdateUserStrategy();

  // Reset local state whenever the modal is (re)opened for a strategy.
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setError(null);
    }
  }, [isOpen, strategyId, currentName]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed !== currentName && !isPending;

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);
    try {
      await mutateAsync({ id: strategyId, data: { name: trimmed } });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename strategy');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rename Strategy" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label htmlFor="rename-strategy-input" style={{ fontSize: 12, color: '#999' }}>
          New name
        </label>
        <input
          id="rename-strategy-input"
          aria-label="New name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
          autoFocus
          style={{
            width: '100%',
            padding: '8px 10px',
            background: '#252525',
            border: '1px solid #444',
            borderRadius: 6,
            color: '#e0e0e0',
            fontSize: 13,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {error && (
          <div style={{ fontSize: 12.5, color: '#f87171' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 14px',
              background: 'none',
              border: '1px solid #444',
              borderRadius: 6,
              color: '#aaa',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              padding: '7px 16px',
              background: canSave ? '#2196f3' : '#333',
              border: 'none',
              borderRadius: 6,
              color: canSave ? '#fff' : '#666',
              fontSize: 13,
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
